import React, {useEffect, useRef} from "react";
import {Popover, PopoverButton, PopoverPanel} from "@headlessui/react";
import {FaPlus, FaXmark} from "react-icons/fa6";

const PopoverStateObserver = ({open, onOpenChange}) => {
    const previousOpen = useRef(open);

    useEffect(() => {
        if (previousOpen.current !== open) onOpenChange?.(open);
        previousOpen.current = open;
    }, [onOpenChange, open]);

    return null;
};

const CardDetailPopover = ({title, panelTitle = title, onRemove, onOpenChange, summary, openOnSummaryClick = false, openOnMount = false, onOpenOnMount, children, readOnly = false}) => {
    const triggerRef = useRef(null);
    const hasOpenedOnMountRef = useRef(false);

    useEffect(() => {
        if (!openOnMount || hasOpenedOnMountRef.current) return;
        hasOpenedOnMountRef.current = true;
        triggerRef.current?.click();
        onOpenOnMount?.();
    }, [onOpenOnMount, openOnMount]);

    if (readOnly) return (
        <section className="relative w-fit max-w-full">
            <h4 className="mb-1.5 text-xs font-semibold text-slate-500">{title}</h4>
            <div className="flex min-h-8 flex-wrap items-center gap-2">
                {React.Children.map(summary, child => React.isValidElement(child) && child.type === "button"
                    ? React.cloneElement(child, {disabled: true, tabIndex: -1})
                    : child)}
            </div>
        </section>
    );

    return (
        <section className="group relative w-fit max-w-full">
        <div className="mb-1.5 flex items-center gap-2">
            <h4 className="text-xs font-semibold text-slate-500">{title}</h4>
            {onRemove && (
                <button type="button" onClick={onRemove} className="rounded p-0.5 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 focus:opacity-100" title={`Excluir ${panelTitle.toLowerCase()}`} aria-label={`Excluir ${panelTitle.toLowerCase()}`}>
                    <FaXmark />
                </button>
            )}
        </div>

        <Popover className="static">
            {({open}) => (
                <>
                    <PopoverStateObserver open={open} onOpenChange={onOpenChange} />
                    <div className="flex min-h-8 flex-wrap items-center gap-2">
                        {openOnSummaryClick
                            ? React.Children.map(summary, child => React.isValidElement(child) ? React.cloneElement(child, {
                                onClick: event => {
                                    child.props.onClick?.(event);
                                    if (!event.defaultPrevented && !open) triggerRef.current?.click();
                                },
                            }) : child)
                            : summary}
                        <PopoverButton ref={triggerRef} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-slate-50 text-slate-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300" aria-label={`Gerenciar ${title.toLowerCase()}`}>
                            <FaPlus />
                        </PopoverButton>
                    </div>
                    <PopoverPanel transition className="absolute left-0 top-full z-40 mt-2 w-80 max-w-[calc(100vw-3rem)] origin-top-left rounded-lg border border-slate-200 bg-white p-3 shadow-xl transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0">
                        {({close}) => (
                            <>
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <h5 className="text-sm font-semibold text-slate-700">{panelTitle}</h5>
                                    <button type="button" onClick={close} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Fechar menu"><FaXmark /></button>
                                </div>
                                {typeof children === "function" ? children({close}) : children}
                            </>
                        )}
                    </PopoverPanel>
                </>
            )}
        </Popover>
        </section>
    );
};

export default CardDetailPopover;
